import { useEvent, convertToMaxDecimals } from "@oysterhr/common"
import { useState } from "react"

export interface UsePercentageContributionFieldControllerArgs {
  getTotalCostAmount: () => string | undefined | null
  setContributionAmount: (amount: string) => void
  defaultContributionPercentage?: number
}

const NUMBERS_AND_DOTS_REGEX = /(\d|\.)*/

const percentageInputToNumber = (percentageInput: string) => {
  const percentageNumber = Number(percentageInput)
  return isNaN(percentageNumber) || percentageInput.trim().length === 0 ? 100 : percentageNumber
}

export function usePercentageContributionFieldController({
  defaultContributionPercentage,
  setContributionAmount,
  getTotalCostAmount,
}: UsePercentageContributionFieldControllerArgs) {
  const [contributionPercentageInputValue, setContributionPercentageInputValue] = useState(
    String(defaultContributionPercentage ?? 100),
  )

  const onChangeContributionPercentageInput = useEvent((value: string) => {
    if (!NUMBERS_AND_DOTS_REGEX.test(value)) return
    setContributionPercentageInputValue(value)

    const percentageNumber = percentageInputToNumber(value)
    const totalPriceAmount = getTotalCostAmount()

    if (totalPriceAmount) {
      const contributionAmount = String((Number(totalPriceAmount) * percentageNumber) / 100)
      setContributionAmount(convertToMaxDecimals(contributionAmount, 2))
    }
  })

  const onChangeTotalCostAmount = useEvent((totalCostAmount: string | undefined) => {
    if (!totalCostAmount?.trim()) {
      setContributionAmount("0")
      return
    }

    const percentageNumber = percentageInputToNumber(contributionPercentageInputValue)
    const totalCostAmountNumber = Number(totalCostAmount)
    const contributionAmount = String((totalCostAmountNumber * percentageNumber) / 100)

    setContributionAmount(convertToMaxDecimals(contributionAmount, 2))
  })

  const onChangeContributionAmount = useEvent((contributionAmount: string | undefined) => {
    if (!contributionAmount?.trim()) {
      setContributionPercentageInputValue("0")
      return
    }

    const totalCostAmountValue = getTotalCostAmount()
    if (!totalCostAmountValue?.trim()) {
      return
    }

    const totalCostAmountNumber = Number(totalCostAmountValue)

    const contributionAmountNumber = Number(contributionAmount)

    setContributionPercentageInputValue(
      String((contributionAmountNumber / totalCostAmountNumber) * 100),
    )
  })

  return {
    onChangeContributionPercentageInput,
    onChangeTotalCostAmount,
    onChangeContributionAmount,
    percentageInputValue: contributionPercentageInputValue,
  }
}
